import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authStorage } from '../../utils/storage';
import PageHead from '../general/PageHead';
import { Search, Download, Upload, Edit } from 'lucide-react';

function UnitManagement() {
  useNavigate();
  const [units, setUnits] = useState([]);
  const [staffReps, setStaffReps] = useState([]);
  const [serviceReps, setServiceReps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deletingUnit, setDeletingUnit] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [towns, setTowns] = useState([]);
  const [saving, setSaving] = useState({});

  const userRole = authStorage.getRole();
  const isAdmin = userRole === 'Admin' || userRole === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = authStorage.getToken();
        setLoading(true);
        const [townsRes, unitsRes, staffRes, serviceRes] = await Promise.all([
          fetch('/api/towns', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/units', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/staff-reps', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/service-reps', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (townsRes.ok) setTowns(await townsRes.json());
        if (staffRes.ok) setStaffReps(await staffRes.json());
        if (serviceRes.ok) setServiceReps(await serviceRes.json());
        if (unitsRes.ok) {
          let unitsData = await unitsRes.json();
          const isManager = userRole === 'Manager' || userRole === 'manager';
          if (!isAdmin && !isManager) {
            unitsData = unitsData.filter(unit => unit.TOWN && unit.TOWN.trim() !== '');
          }
          setUnits(unitsData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin, userRole]);

  const filteredUnits = useMemo(() => {
    if (!searchTerm) return units;
    const q = searchTerm.toLowerCase();
    return units.filter(unit =>
      unit.LOCAL?.toString().toLowerCase().includes(q) ||
      unit.NAME?.toLowerCase().includes(q) ||
      unit.TOWN?.toLowerCase().includes(q)
    );
  }, [searchTerm, units]);

  const unassignedCount = units.filter(u => !u.STAFF_ASSIGNED).length;

  const getStatus = (unit) => {
    if (!unit.STAFF_ASSIGNED) return 'unassigned';
    if (unit.AuditCompleteOverview) {
      const auditDate = new Date(unit.AuditCompleteOverview);
      const daysSince = (Date.now() - auditDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince > 90) return 'overdue';
    }
    return 'active';
  };

  const handleStaffChange = async (unitId, staffId, staffName, field) => {
    setSaving(s => ({ ...s, [unitId]: true }));
    try {
      const token = authStorage.getToken();
      const body = field === 'staff'
        ? { employee1: staffId || '---', employee1Name: staffName || null }
        : { employee3: staffId || '---', employee3Name: staffName || null };
      const res = await fetch(`/api/assign-employees/${unitId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setUnits(prev => prev.map(u => {
          if (u.id !== unitId) return u;
          if (field === 'staff') return { ...u, STAFF_ASSIGNED: staffName || null, STAFF_ASSIGNED_ID: staffId || null };
          return { ...u, ServiceFieldRep1: staffName || null, ServiceFieldRep1_Id: staffId || null };
        }));
      }
    } catch (err) {
      console.error('Error saving staff assignment:', err);
    } finally {
      setSaving(s => ({ ...s, [unitId]: false }));
    }
  };

  const handleEdit = (unit) => {
    setEditingId(unit.id);
    setEditData({ LOCAL: unit.LOCAL, UNIT: unit.UNIT || '', NAME: unit.NAME, TOWN: unit.TOWN });
  };

  const handleCancelEdit = () => { setEditingId(null); setEditData({}); };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (unitId) => {
    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/bargaining-units/${unitId}/basic-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editData),
      });
      const data = await response.json();
      if (response.ok) {
        setUnits(units.map(unit => unit.id === unitId ? { ...unit, ...editData } : unit));
        setEditingId(null);
        setEditData({});
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(data.error || 'Failed to update unit');
      }
    } catch (err) {
      setError('An error occurred while updating the unit');
    }
  };





  const handleCancelDelete = () => {
    setDeletingUnit(null);
    setDeleteConfirmName('');
    setDeleteConfirmCode('');
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingUnit) return;
    if (deleteConfirmName.trim().toUpperCase() !== deleteConfirmCode.toUpperCase()) {
      setError('The confirmation code does not match. Please try again.');
      return;
    }
    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/bargaining-units/${deletingUnit.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to delete unit');
        return;
      }
      setUnits(units.filter(unit => unit.id !== deletingUnit.id));
      setDeletingUnit(null);
      setDeleteConfirmName('');
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setError(''); }, 3000);
    } catch (err) {
      setError('An error occurred while deleting the unit. Please try again.');
    }
  };

  const staffOptions = [
    { EmployeeId: '', full_name: '—' },
    ...staffReps
  ];
  const serviceOptions = [
    { EmployeeId: '', full_name: '—' },
    ...serviceReps
  ];

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          table.tbl td select { width: 100%; max-width: none; min-height: 40px; }
        }
      `}</style>
      <PageHead
        title="Unit management"
        sub="Assign staff, area coordinators, and routing for each local"
        actions={
          <>
            <button className="btn btn-secondary"><Upload size={14} /> Import CSV</button>
            <button className="btn btn-secondary"><Download size={14} /> Export</button>
          </>
        }
      />

      {success && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, background: 'var(--color-success-soft)',
          color: '#1b6b45', marginBottom: 16, fontSize: 13.5
        }}>
          Operation completed successfully.
        </div>
      )}

      <div className="toolbar list-toolbar" style={{ flexWrap: 'wrap', marginBottom: 18 }}>
        <div className="search-box" style={{ width: 280, flexShrink: 0 }}>
          <Search className="ic" size={14} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search units…"
          />
        </div>
        <span className="tiny muted" style={{ marginLeft: 8, flexShrink: 0 }}>{filteredUnits.length} of {units.length} units</span>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          {unassignedCount > 0 && (
            <span className="badge badge-warning"><span className="d"></span>{unassignedCount} unassigned</span>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Local</th>
                <th>Name</th>
                <th>Staff Rep</th>
                <th>Service Rep</th>
                <th>Area Coordinator</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-3)' }}>Loading units…</td>
                </tr>
              ) : filteredUnits.map(u => {
                const status = getStatus(u);
                return (
                  <tr key={u.id}>
                    <td className="mono" data-label="Local" style={{ whiteSpace: 'nowrap' }}>
                      {u.LOCAL}-{String(u.UNIT || 0).padStart(4, '0')}
                    </td>
                    <td className="card-title" data-label="Name" style={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.NAME}
                    </td>
                    <td data-label="Staff Rep" style={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                      <select
                        className="select"
                        style={{ padding: '5px 8px', fontSize: 12.5, width: 150, maxWidth: 150 }}
                        value={u.STAFF_ASSIGNED_ID || ''}
                        disabled={saving[u.id]}
                        onChange={e => {
                          const opt = staffOptions.find(s => String(s.EmployeeId) === e.target.value);
                          handleStaffChange(u.id, e.target.value, opt?.full_name || null, 'staff');
                        }}
                      >
                        {staffOptions.map(s => (
                          <option key={s.EmployeeId} value={s.EmployeeId}>{s.full_name}</option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Service Rep" style={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                      <select
                        className="select"
                        style={{ padding: '5px 8px', fontSize: 12.5, width: 150, maxWidth: 150 }}
                        value={u.ServiceFieldRep1_Id || ''}
                        disabled={saving[u.id]}
                        onChange={e => {
                          const opt = serviceOptions.find(s => String(s.EmployeeId) === e.target.value);
                          handleStaffChange(u.id, e.target.value, opt?.full_name || null, 'service');
                        }}
                      >
                        {serviceOptions.map(s => (
                          <option key={s.EmployeeId} value={s.EmployeeId}>{s.full_name}</option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Area Coordinator" style={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                      <select
                        className="select"
                        style={{ padding: '5px 8px', fontSize: 12.5, width: 150, maxWidth: 150 }}
                        defaultValue=""
                      >
                        <option value="">—</option>
                      </select>
                    </td>
                    <td data-label="Status" style={{ whiteSpace: 'nowrap' }}>
                      {status === 'active' && <span className="badge badge-success"><span className="d"></span>Active</span>}
                      {status === 'overdue' && <span className="badge badge-warning"><span className="d"></span>Overdue</span>}
                      {status === 'unassigned' && <span className="badge badge-neutral"><span className="d"></span>Unassigned</span>}
                    </td>
                    <td data-label="Edit">
                      <button className="btn-icon" onClick={() => handleEdit(u)}><Edit size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {error && !deletingUnit && (
          <div style={{ padding: '10px 16px', color: '#8a2d2d', background: 'var(--color-danger-soft)', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5 }}>
            {error}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editingId && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius)', width: 'min(480px, 92vw)',
            boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-subtle)'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Edit Unit</span>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="dh-field">
                <label className="dh-label">Local</label>
                <input className="dh-input" type="text" name="LOCAL" value={editData.LOCAL} onChange={handleEditChange} />
              </div>
              <div className="dh-field">
                <label className="dh-label">Unit</label>
                <input className="dh-input" type="number" name="UNIT" value={editData.UNIT} onChange={handleEditChange} />
              </div>
              <div className="dh-field">
                <label className="dh-label">Name</label>
                <input className="dh-input" type="text" name="NAME" value={editData.NAME} onChange={handleEditChange} />
              </div>
              <div className="dh-field">
                <label className="dh-label">Town</label>
                <select className="dh-select" name="TOWN" value={editData.TOWN} onChange={handleEditChange} style={{ width: '100%' }}>
                  {towns.map(town => (
                    <option key={town.Name} value={town.Name}>{town.Name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={handleCancelEdit}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleSaveEdit(editingId)}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deletingUnit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius)', width: 'min(480px, 92vw)',
            boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-subtle)'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 16 }}>Delete Unit</span>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ marginBottom: 8, fontSize: 14, color: 'var(--fg-1)' }}>
                Are you sure you want to delete this unit?
              </p>
              <div style={{ padding: '10px 14px', background: 'var(--bg-sunken)', borderRadius: 6, fontWeight: 600, marginBottom: 16 }}>
                {deletingUnit.NAME} (Local {deletingUnit.LOCAL})
              </div>
              <p style={{ color: 'var(--fg-2)', fontSize: 13.5, marginBottom: 8 }}>
                This action cannot be undone. Type this code to confirm:
              </p>
              <div style={{
                fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 20, color: '#dc2626',
                letterSpacing: 3, padding: '8px 14px', background: '#fef2f2', borderRadius: 6, marginBottom: 16,
                textAlign: 'center'
              }}>
                {deleteConfirmCode}
              </div>
              <input
                className="dh-input"
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Enter confirmation code…"
                style={{ textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center', fontSize: 16, width: '100%' }}
                autoFocus
              />
              {error && deleteConfirmName && (
                <div style={{ color: '#dc2626', marginTop: 8, fontSize: 13 }}>{error}</div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={handleCancelDelete}>Cancel</button>
              <button
                className="btn"
                style={{
                  background: deleteConfirmName.trim().toUpperCase() === deleteConfirmCode.toUpperCase() ? '#dc2626' : 'var(--bg-sunken)',
                  color: deleteConfirmName.trim().toUpperCase() === deleteConfirmCode.toUpperCase() ? '#fff' : 'var(--fg-3)',
                  border: 'none',
                  cursor: deleteConfirmName.trim().toUpperCase() === deleteConfirmCode.toUpperCase() ? 'pointer' : 'not-allowed',
                }}
                onClick={handleConfirmDelete}
                disabled={deleteConfirmName.trim().toUpperCase() !== deleteConfirmCode.toUpperCase()}
              >
                Delete Unit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UnitManagement;
